import React, { useEffect, useState } from "react";
import Form from "../../../form/Form";
import Card from "../../../elements/Card";
import Input from "../../../form/Input";
import Button from "../../../form/Button";
import Image from "../../../elements/Image";
import Tooltip from "../../../elements/Tooltip";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useForm from "../../../../hooks/useForm";
import {
  profileDetails,
  profileImageDelete,
  publicProfileDetails,
  publicProfileUpdate,
} from "../../../../services/ProfileService";
import DeleteModal from "../../../common/DeleteModal";
import dayjs from "dayjs";
import Textarea from "../../../form/Textarea";
import { useSelector } from "react-redux";
import Badge from "../../../elements/Badge";
import { t } from "i18next";

const PublicProfile = () => {
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { selectedOrganization, organizations } = useSelector(
    (state) => state?.organization
  );

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({});

  const { data: profileData } = useQuery({
    queryKey: ["public-profile"],
    queryFn: async () => {
      const res = await publicProfileDetails();
      return res;
    },
  });

  useEffect(() => {
    if (profileData?.data?._id) {
      setFieldsValue({
        displayName: profileData?.data?.info?.displayName,
        profileData: profileData?.data?.info?.profileData,
        image: profileData?.data?.info?.avatarUrl,
      });
    }
  }, [profileData?.data]);

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: async (value) => {
      const payload = {
        ...(value?.image instanceof File && {
          image: value?.image,
        }),
        organization: selectedOrganization,
        info: {
          ...(typeof value?.image === "string" && {
            avatarUrl: value?.image,
          }),
          displayName: value?.displayName,
          profileData: value?.profileData,
          provider: "public",
        },
      };
      return await publicProfileUpdate(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        queryClient.setQueryData(["public-profile"], (state) => {
          const state_data = JSON.parse(JSON.stringify(state || {}));
          state_data.data = res?.data;
          return { ...state_data };
        });
      }
    },
  });

  const { mutate: removeImageHandler, isPending: imageRemovePending } =
    useMutation({
      mutationFn: async (img) => {
        const payload = {
          organization: selectedOrganization,
          info: {
            displayName: values?.displayName,
            profileData: values?.profileData,
            provider: "public",
          },
        };
        return await publicProfileUpdate(payload);
      },
      onSuccess: (res) => {
        if (res?.status === 200 || res?.status === 201) {
          setShowDeleteModal(false);
          queryClient.setQueryData(["public-profile"], (state) => {
            const state_data = JSON.parse(JSON.stringify(state || {}));
            state_data.data = res?.data;
            return { ...state_data };
          });
        }
      },
    });

  return (
    <>
      <Form handleSubmit={handleSubmit} onSubmit={onSubmit}>
        <div className="bg-white py-4 px-4 rounded-md">
          <div className="space-y-6">
            <Card
              title={
                <div className="flex items-center justify-between gap-2">
                  {t("Public profile")}
                  {profileData?.data?.status && (
                    <Badge
                      label={t(
                        profileData?.data?.status === "active"
                          ? "accepted"
                          : "pending"
                      )}
                      type={
                        profileData?.data?.status === "active"
                          ? "success"
                          : "danger"
                      }
                      className={"text-base"}
                    />
                  )}
                </div>
              }
              bodyClass={"space-y-6"}
              className={"py-6 px-3 md:px-6 lg:px-9 lg:py-9"}
            >
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-4 md:col-span-3">
                  <div className="relative group">
                    <label className="relative rounded-lg !overflow-hidden">
                      <div className="border border-gray-100 aspect-square">
                        <Image
                          src={
                            values?.image || values?.image?.url
                              ? (values?.image || values?.image?.url) instanceof
                                File
                                ? URL.createObjectURL(
                                    values?.image || values?.image?.url
                                  )
                                : values?.image ||
                                  values?.image?.url ||
                                  require("../../../../../src/assets/images/dummy-user.png")
                              : require("../../../../../src/assets/images/dummy-user.png")
                          }
                          alt="Uploaded Image"
                          classes="object-cover"
                        />
                      </div>
                      <input
                        type="file"
                        className="h-0 w-0 opacity-0 absolute top-0 left-0"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          const type = file?.type?.split("/")[0];
                          if (type === "image") {
                            handleChange({
                              target: { name: "image", value: file },
                            });
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {(values?.image || values?.image?.url) && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className="flex flex-col gap-3 opacity-0 group-hover:opacity-100 transition-all duration-300 absolute top-0 left-0 w-full h-full items-center justify-center"
                      >
                        <Tooltip message={t("Delete this photo")}>
                          <Button
                            buttonClasses="w-full bg-red-50 text-red-600 hover:bg-red-100 rounded-md px-3 py-2 text-sm font-medium shadow-sm transition"
                            buttonIcon="fa-light fa-trash-can"
                            buttonIconPosition="left"
                            // buttonLabel="Delete"
                            buttonFunction={() => {
                              if (
                                values?.image &&
                                !(values?.image instanceof File)
                              ) {
                                setShowDeleteModal(true);
                              } else {
                                setFieldsValue({ image: null });
                              }
                            }}
                          />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-8 space-y-3">
                  <Input
                    inputName={"displayName"}
                    inputPlaceholder={t("Enter full name")}
                    onChange={handleChange}
                    value={values?.displayName || ""}
                    label={t("Name")}
                    {...formValidate(errors, "displayName")}
                  />
                  <Textarea
                    inputPlaceholder={t("Enter bio")}
                    onChange={handleChange}
                    inputName={"profileData"}
                    label={"Bio"}
                    inputValue={values?.profileData || ""}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <Button
            buttonLabel={t("Save")}
            buttonType="submit"
            {...(isPending && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </Form>
      <DeleteModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        deleteHandler={() => removeImageHandler(values?.image)}
        customMessage={"Are you sure you want to delete this image?"}
        loading={imageRemovePending}
      />
    </>
  );
};

export default PublicProfile;
